import { computed } from 'vue'
import type {
  ProductSearchQuery,
  AttributeWithBooleanValueFilter,
  AttributeWithValuesFilter,
} from '@scayle/storefront-nuxt'
import type { LocationQuery } from 'vue-router'
import { useRoute } from '#app/composables/router'

/** Query params that are not Odoscope filters */
const IGNORED_QUERY_PARAMS = new Set([
  'term',
  'page',
  'sort',
  'q',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'dclid',
  'twclid',
  'li_fat_id',
  'ref',
])

export default (_query?: LocationQuery) => {
  const route = useRoute()

  const query = computed(() => _query || route.query)

  const getAttributeValues = (value: string): Array<string> => {
    return value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
  }

  const parseFilterData = () => {
    const queryKeys = Object.keys(query.value)
    const productSearchQuery: ProductSearchQuery = { attributes: [] }

    queryKeys.forEach((queryKey: string) => {
      const value = query.value[queryKey] as string

      if (!value || IGNORED_QUERY_PARAMS.has(queryKey)) {
        return
      }

      const filterName = queryKey

      switch (filterName) {
        case 'minPrice':
        case 'maxPrice':
          if (isNaN(Number(value))) {
            return
          }
          productSearchQuery[filterName] = Number(value)
          break
        default:
          if (value === 'true' || value === 'false') {
            const booleanFilter: AttributeWithBooleanValueFilter = {
              type: 'boolean',
              key: filterName,
              value: value === 'true',
            }
            return productSearchQuery.attributes?.push(booleanFilter)
          } else {
            const values = getAttributeValues(value)

            if (values.length === 0) {
              return
            }

            const attributeFilter: AttributeWithValuesFilter = {
              type: 'attributes',
              key: filterName,
              values: values as any, // String values for Odoscope
            }

            return productSearchQuery.attributes?.push(attributeFilter)
          }
      }
    })

    return productSearchQuery
  }

  const appliedFilter = computed<ProductSearchQuery>(() => {
    return parseFilterData()
  })

  const appliedFiltersCount = computed<number>(() => {
    let count = 0

    // Add all of the different attribute/boolean filters to the count
    count += appliedFilter.value.attributes?.length ?? 0

    // Count the minPrice/maxPrice filters as one filter
    if (
      appliedFilter.value.minPrice !== undefined ||
      appliedFilter.value.maxPrice !== undefined
    ) {
      count += 1
    }

    return count
  })

  const appliedAttributeValues = computed(() => {
    const values: Record<string, Array<string>> = {}

    appliedFilter.value?.attributes?.forEach((attribute) => {
      if (attribute.type !== 'attributes' || !attribute.key) {
        return
      }

      values[attribute.key] = attribute.values as string[]
    })
    return values
  })

  const appliedBooleanValues = computed(() => {
    const values: Record<string, boolean> = {}

    appliedFilter.value?.attributes?.forEach((attribute) => {
      if (attribute.type === 'boolean') {
        values[attribute.key] = attribute.value
      }
    })
    return values
  })

  const areFiltersApplied = computed<boolean>(() => {
    return appliedFiltersCount.value > 0
  })

  return {
    appliedFilter,
    appliedFiltersCount,
    appliedAttributeValues,
    appliedBooleanValues,
    areFiltersApplied,
  }
}
